import React from 'react';
import { getVehicleChecklistConfig, getVehicleChecklistTextFields } from './vehicleChecklistConfig';

const FIELD_GROUPS = [
  {
    title: 'Contexto de la operación',
    fields: ['project', 'area'],
  },
  {
    title: 'Información general del vehículo',
    fields: ['owner_identification', 'color', 'engine_number', 'chassis_number', 'owner_name', 'passenger_capacity'],
  },
  {
    title: 'Datos generales del conductor',
    fields: ['driver_role', 'driver_license_number', 'driver_license_categories', 'driver_license_expiry'],
  },
  {
    title: 'Inventario',
    fields: ['initial_mileage', 'final_mileage', 'next_oil_change_km'],
  },
];

const textFieldMap = Object.fromEntries(getVehicleChecklistTextFields().map((field) => [field.key, field.label]));

function ReadOnlyField({ label, value }) {
  return (
    <div className="rounded border border-gray-200 bg-gray-50 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-sm text-gray-900">{String(value || '-')}</div>
    </div>
  );
}

function PhotoGallery({ photos }) {
  if (!Array.isArray(photos) || photos.length === 0) {
    return <div className="rounded border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-500">Sin fotos registradas.</div>;
  }

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
      {photos.map((photo, index) => (
        <a
          key={`${photo.name || 'foto'}-${index}`}
          href={photo.dataUrl}
          target="_blank"
          rel="noreferrer"
          className="overflow-hidden rounded-lg border border-gray-200 bg-white"
        >
          <img
            src={photo.dataUrl}
            alt={photo.name || `Foto ${index + 1}`}
            className="h-36 w-full object-cover"
          />
          <div className="truncate border-t border-gray-100 p-2 text-xs text-gray-600">
            {photo.name || `Foto ${index + 1}`}
          </div>
        </a>
      ))}
    </div>
  );
}

export default function VehicleChecklistViewer({ checklist, vehicleType, modeLabel }) {
  const config = getVehicleChecklistConfig(vehicleType || checklist?.vehicle_type || '');
  const findingsText = checklist?.findings_notes || (Array.isArray(checklist?.hallazgos) ? checklist.hallazgos.filter(Boolean).join('\n') : '');

  if (!checklist || !config) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
        No hay checklist disponible para visualizar.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-orange-700">{config.code}</div>
        <h4 className="mt-1 text-base font-semibold text-gray-900">{config.title}</h4>
        <p className="mt-1 text-sm text-gray-600">
          Detalle guardado para <span className="font-semibold">{modeLabel.toLowerCase()}</span> del vehículo tipo{' '}
          <span className="font-semibold">{vehicleType || checklist?.vehicle_type}</span>.
        </p>
      </div>

      {FIELD_GROUPS.map((group) => (
        <div key={group.title} className="rounded-lg border border-gray-200 bg-white p-4">
          <h5 className="mb-3 text-sm font-semibold text-gray-900">{group.title}</h5>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {group.fields.map((key) => (
              <ReadOnlyField key={key} label={textFieldMap[key] || key} value={checklist?.text?.[key]} />
            ))}
            {group.title === 'Inventario' && (
              <>
                <ReadOnlyField label="Tipo de combustible" value={checklist?.fuel_type} />
                <ReadOnlyField label="Hoja de vida del vehículo" value={checklist?.has_vehicle_life_sheet} />
                <ReadOnlyField label="Carpeta de documentos del vehículo" value={checklist?.has_document_folder} />
              </>
            )}
          </div>
        </div>
      ))}

      {config.sections.map((section) => (
        <div key={section.title} className="rounded-lg border border-gray-200 bg-white p-4">
          <h5 className="mb-3 text-sm font-semibold text-gray-900">{section.title}</h5>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {section.items.map((item) => (
              <ReadOnlyField key={item.key} label={item.label} value={checklist?.statuses?.[item.key]} />
            ))}
          </div>
        </div>
      ))}

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h5 className="mb-3 text-sm font-semibold text-gray-900">Estado físico del vehículo</h5>
        <div className="space-y-4">
          <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900 whitespace-pre-wrap">
            {checklist?.physical_condition_notes || '-'}
          </div>
          <PhotoGallery photos={checklist?.physical_condition_photos} />
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h5 className="mb-3 text-sm font-semibold text-gray-900">Llantas</h5>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          <ReadOnlyField label="Referencia llantas delanteras" value={checklist?.tire_data?.front_reference} />
          <ReadOnlyField label="Tipo llantas delanteras" value={checklist?.tire_data?.front_type} />
          <ReadOnlyField label="Referencia llantas traseras" value={checklist?.tire_data?.rear_reference} />
          <ReadOnlyField label="Tipo llantas traseras" value={checklist?.tire_data?.rear_type} />
          <ReadOnlyField label="Referencia llanta de repuesto" value={checklist?.tire_data?.spare_reference} />
          <ReadOnlyField label="Tipo llanta de repuesto" value={checklist?.tire_data?.spare_type} />
          <ReadOnlyField label="Labrado delantera izquierda" value={checklist?.tire_data?.tread_front_left} />
          <ReadOnlyField label="Labrado delantera derecha" value={checklist?.tire_data?.tread_front_right} />
          <ReadOnlyField label="Labrado trasera izquierda" value={checklist?.tire_data?.tread_rear_left} />
          <ReadOnlyField label="Labrado trasera derecha" value={checklist?.tire_data?.tread_rear_right} />
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h5 className="mb-3 text-sm font-semibold text-gray-900">Hallazgos</h5>
        <div className="space-y-4">
          <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900 whitespace-pre-wrap">
            {findingsText || '-'}
          </div>
          <PhotoGallery photos={checklist?.findings_photos} />
        </div>
      </div>
    </div>
  );
}
